import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {CartStore} from '../stores/cart.store';
import {WorkDay, WorkTime} from '../models/restaurant.model';
import {FulfillmentTypeItem} from '../../pages/checkout/models/location.model';
import {FulfillmentType} from '../models/order.model';
import {PaymentStore} from '../stores/payment.store';
import { Variation } from '../models/catalog.model';

@Injectable({
  providedIn: 'root'
})
export class ItemValidateService {

  constructor(private cartStore: CartStore,
              private paymentStore: PaymentStore) {
  }

  public validLeadTime(variation: Variation, hoursOfOperation: WorkDay[]): boolean {
    const currentTime = new Date();
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();
    const validatedRanges = [];
    if (!hoursOfOperation ||  !hoursOfOperation[pickupTime?.getDay()]) {
      return !!validatedRanges.find(validRange => validRange);
    }
      for (const [index] of hoursOfOperation[pickupTime.getDay()].startTime.entries()) {
        const storeOpeningTime = hoursOfOperation[pickupTime.getDay()].startTime[index].getDate(pickupTime);
        const storeClosingTime = hoursOfOperation[pickupTime.getDay()].endTime[index].getDate(pickupTime);

        const checkBeforePickupTime = currentTime.getTime() <= pickupTime.getTime() - this.convertFromLeadTime(variation.pickup_lead_time);
        const checkBeforeOpening = this.convertFromLeadTime(variation.opening_lead_time) === 0 ||
          currentTime.getTime() <= storeOpeningTime.getTime() - this.convertFromLeadTime(variation.opening_lead_time);
        const checkBeforeClosing = pickupTime.getTime() <= storeClosingTime.getTime() - this.convertFromLeadTime(variation.closing_lead_time);

        validatedRanges.push(checkBeforePickupTime && checkBeforeOpening && checkBeforeClosing);
      }
    return !!validatedRanges.find(validRange => validRange);
  }

  public toMinutes(wt: WorkTime): number  {
    if (!wt || !wt.time) return 0;
    const m = wt.time.minute ?? 0;
    // normalize 12h: 12 -> 0, then add 12h if PM
    const baseHour = (wt.time.hour ?? 0) % 12;
    const hour24 = wt.meridian === true /* AM */ ? baseHour : baseHour + 12;
    return hour24 * 60 + m;
  };

  public validDayRestriction(variation: Variation): boolean {
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();

    if (variation.daily_availability == null) {
      return true;
    }

    for (const day of variation.daily_availability) {
      if (day.value === pickupTime.getDay() && day.enabled) {
        if (day.startTime === null || day.endTime === null) {
          return true;
        }
        const start = this.toMinutes(day.startTime);
        const end = this.toMinutes(day.endTime);
        const pickup = pickupTime.getHours() * 60 + pickupTime.getMinutes();

        // same-day window vs. overnight window
        return (start <= end)
          ? (start <= pickup && pickup <= end)
          : (pickup >= start || pickup <= end);
      }
    }
    return false;
  }

  public validFulfillmentRestriction(variation: Variation, fulfillment: FulfillmentTypeItem, isTab: boolean = false): boolean {
    let fulfillmentType = fulfillment?.title;
    if (fulfillment?.type === FulfillmentType.HANDOFF) {
      fulfillmentType = 'X-Side';
    }
    if (this.paymentStore.createTabChecked || isTab) {
      fulfillmentType = 'Tabs';
    }
    const variationValidFulfillments = variation.fulfillments;

    return !!variationValidFulfillments.find((result: string) => { return fulfillmentType?.toLowerCase() === result?.toLowerCase(); });
  }

  public validItemDatetimeRange(variation: Variation): boolean {
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();
    return variation.available_from < pickupTime && variation.available_until > pickupTime;
  }

  public validFulfillments(variation: Variation, fulfillments: string[]): boolean {
    const variationValidFulfillments = variation.fulfillments;
    const availableFulfillments = [];

    for (const fulfillment of fulfillments) {
      availableFulfillments.push(!!variationValidFulfillments.find((result: string) => { return fulfillment.toLowerCase() === result.toLowerCase(); }));
    }

    return !!availableFulfillments.find((result: boolean) => { return result === true; });
  }

  public validItemRestaurantWorkHours(hoursOfOperation: WorkDay[]): boolean {
    const pickup = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();
    const chosenDay = hoursOfOperation.find( (day) => moment(day.date).isSame(pickup, 'day'));
    if (!chosenDay || !chosenDay.enable) {
      return false;
    }
    for (const [index] of chosenDay.startTime.entries()) {
      const rangeStartTime = this.convertWorkTimeToMinutes(chosenDay.startTime[index]);
      const rangeEndTime = this.convertWorkTimeToMinutes(chosenDay.endTime[index]);
      const pickupTime = pickup.getHours() * 60 + pickup.getMinutes();
      if (rangeStartTime <= pickupTime && rangeEndTime >= pickupTime) {
        return true;
      }
    }
    return false;
  }

  public convertLeadTimeToMinutes(value: string): number {
    const timeInMillis = this.convertFromLeadTime(value);
    return Math.ceil(timeInMillis / 60000);
  }

  private convertWorkTimeToMinutes(workTime: WorkTime): number {
    const meridian = workTime.meridian ? 0 : 12;
    const hours = workTime.time.hour === 12 ? 0 : workTime.time.hour;
    return (hours + meridian) * 60 + workTime.time.minute;
  }

  private convertFromLeadTime(value: string): number {
    return moment.duration(value).asMilliseconds();
  }
}
